/**
 * Data service for managing data export, import, and backup operations
 */
import customerService from './CustomerService';
import treatmentService from './TreatmentService';
import practitionerService from './PractitionerService';
import appointmentService from './AppointmentService';
import treatmentRecordService from './TreatmentRecordService';
import venueService from './VenueService';
import * as LocalStorage from '../utils/localStorage';
import {
  mockCustomers,
  mockPractitioners,
  mockTreatments,
  mockVenues,
  mockAppointments,
  mockTreatmentRecords,
  mockWorkOrders
} from '../data';

class DataService {
  /**
   * Constructor initializes the service
   */
  constructor() {
    this.services = {
      customers: customerService,
      treatments: treatmentService,
      practitioners: practitionerService,
      appointments: appointmentService,
      treatmentRecords: treatmentRecordService,
      venues: venueService,
    };
  }

  /**
   * Export all data as a JSON file
   * @returns {Object} Export result with data and timestamp
   */
  exportAllData() {
    try {
      const exportData = {};
      const timestamp = new Date().toISOString();

      // Export data from each service
      Object.keys(this.services).forEach(serviceKey => {
        const service = this.services[serviceKey];
        if (typeof service.exportData === 'function') {
          exportData[serviceKey] = JSON.parse(service.exportData());
        } else if (service.storageService) {
          exportData[serviceKey] = JSON.parse(service.storageService.exportData());
        }
      });

      // Add metadata
      const result = {
        metadata: {
          exportDate: timestamp,
          version: '1.0',
          entity: 'Chinese Medicine Therapy Center'
        },
        data: exportData
      };

      return {
        success: true,
        data: JSON.stringify(result, null, 2),
        timestamp
      };
    } catch (error) {
      console.error('Error exporting all data:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Export data as file for download
   * @returns {Object} Export result with blob and filename
   */
  exportDataAsFile() {
    try {
      const exportResult = this.exportAllData();
      if (!exportResult.success) {
        return exportResult;
      }

      // Create a blob from the data
      const blob = new Blob([exportResult.data], { type: 'application/json' });

      // Create a date string for the filename
      const dateStr = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
      const filename = `clinic_data_export_${dateStr}.json`;

      return {
        success: true,
        blob,
        filename,
        timestamp: exportResult.timestamp
      };
    } catch (error) {
      console.error('Error creating export file:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Import data from JSON
   * @param {string} jsonData - JSON string of data
   * @param {boolean} [merge=false] - Whether to merge with existing data
   * @returns {Object} Import result with details
   */
  importData(jsonData, merge = false) {
    try {
      // Parse the JSON data
      const importedData = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;

      // Validate the data structure
      if (!importedData || !importedData.data) {
        throw new Error('无效的数据格式');
      }

      const results = {};
      let hasErrors = false;

      // Import data to each service
      Object.keys(importedData.data).forEach(serviceKey => {
        const serviceData = importedData.data[serviceKey];

        if (this.services[serviceKey]) {
          const service = this.services[serviceKey];
          let success = false;

          if (typeof service.importData === 'function') {
            success = service.importData(JSON.stringify(serviceData), merge);
          } else if (service.storageService) {
            success = service.storageService.importData(serviceData, merge);
          }

          results[serviceKey] = {
            success,
            itemCount: Object.keys(serviceData).length
          };

          if (!success) {
            hasErrors = true;
          }
        }
      });

      return {
        success: !hasErrors,
        details: results,
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error importing data:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Backup all data
   * @param {string} [description=''] - Backup description
   * @returns {Object} Backup result with details
   */
  backupAllData(description = '') {
    try {
      const timestamp = new Date().toISOString();
      const backupKey = `clinic_backup_${timestamp.replace(/[:.]/g, '-')}`;

      // Get export data
      const exportResult = this.exportAllData();
      if (!exportResult.success) {
        return exportResult;
      }

      // Create backup object
      const backupData = {
        timestamp,
        description: description || `自动备份 - ${new Date().toLocaleString('zh-CN')}`,
        data: JSON.parse(exportResult.data)
      };

      // Store backup
      LocalStorage.setItem(backupKey, backupData);

      return {
        success: true,
        backupId: backupKey,
        timestamp,
        description: backupData.description
      };
    } catch (error) {
      console.error('Error creating backup:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * List all available backups
   * @returns {Array} List of backups
   */
  listBackups() {
    try {
      const keys = LocalStorage.getKeys();
      const backupPrefix = 'clinic_backup_';

      const backups = keys
        .filter(key => key.startsWith(backupPrefix))
        .map(key => {
          const backup = LocalStorage.getItem(key);
          return {
            backupId: key,
            timestamp: backup.timestamp,
            description: backup.description
          };
        })
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)); // Sort newest first

      return backups;
    } catch (error) {
      console.error('Error listing backups:', error);
      return [];
    }
  }

  /**
   * Get backup details
   * @param {string} backupId - Backup ID
   * @returns {Object|null} Backup details or null if not found
   */
  getBackupDetails(backupId) {
    try {
      const backup = LocalStorage.getItem(backupId);
      if (!backup) return null;

      // Count items in each service
      const counts = {};
      if (backup.data && backup.data.data) {
        Object.keys(backup.data.data).forEach(serviceKey => {
          const data = backup.data.data[serviceKey];
          counts[serviceKey] = Object.keys(data).length;
        });
      }

      return {
        backupId,
        timestamp: backup.timestamp,
        description: backup.description,
        counts
      };
    } catch (error) {
      console.error('Error getting backup details:', error);
      return null;
    }
  }

  /**
   * Restore from backup
   * @param {string} backupId - Backup ID
   * @param {boolean} [clearFirst=true] - Whether to clear existing data before restore
   * @returns {Object} Restore result with details
   */
  restoreFromBackup(backupId, clearFirst = true) {
    try {
      const backup = LocalStorage.getItem(backupId);
      if (!backup || !backup.data) {
        return {
          success: false,
          message: '备份数据无效或不存在'
        };
      }

      // Import the data with the specified mode (merge or replace)
      return this.importData(backup.data, !clearFirst);
    } catch (error) {
      console.error('Error restoring from backup:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Delete a backup
   * @param {string} backupId - Backup ID
   * @returns {boolean} Whether deletion was successful
   */
  deleteBackup(backupId) {
    try {
      LocalStorage.removeItem(backupId);
      return true;
    } catch (error) {
      console.error('Error deleting backup:', error);
      return false;
    }
  }

  /**
   * Check the system's storage utilization
   * @returns {Object} Storage utilization info
   */
  checkStorageUtilization() {
    try {
      const storageInfo = LocalStorage.getStorageInfo();

      // Count items by type
      const itemCounts = {};
      Object.keys(this.services).forEach(serviceKey => {
        const service = this.services[serviceKey];
        if (service.storageService) {
          itemCounts[serviceKey] = service.storageService.count();
        }
      });

      return {
        ...storageInfo,
        itemCounts
      };
    } catch (error) {
      console.error('Error checking storage utilization:', error);
      return {
        available: false,
        error: error.message
      };
    }
  }

  /**
   * Clear all data
   * @returns {Object} Clear result
   */
  clearAllData() {
    try {
      // Create a backup before clearing
      const backupResult = this.backupAllData('数据清除前的自动备份');

      // Clear data for each service
      const results = {};
      Object.keys(this.services).forEach(serviceKey => {
        const service = this.services[serviceKey];
        if (service.storageService) {
          results[serviceKey] = service.storageService.clear();
        }
      });

      return {
        success: true,
        backupId: backupResult.backupId,
        details: results
      };
    } catch (error) {
      console.error('Error clearing all data:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Generate sample data for testing
   * @returns {Object} Generation result
   */
  generateSampleData() {
    try {
      // Create the data using imported mock data
      mockCustomers.forEach(customer => customerService.createCustomer(customer));

      // Create practitioners with proper data separation
      mockPractitioners.forEach(practitioner => {
        // 主表写入时强制指定 id
        const userData = {
          id: practitioner.id, // 用 mockPractitioner 的 id
          username: practitioner.username,
          password: practitioner.password,
          name: practitioner.name,
          email: practitioner.email,
          phoneNumber: practitioner.phoneNumber,
          phone: practitioner.phoneNumber,
          isActive: practitioner.isActive
        };

        // 详细字段也用同样的 id
        const professionalDetails = {
          ...practitioner
        };

        // Create practitioner (user account)
        const result = practitionerService.createPractitioner(userData);
        if (result.success) {
          // Set professional details，强制用 id
          practitionerService.setPractitionerDetails(userData.id, professionalDetails);
        }
      });

      mockTreatments.forEach(treatment => treatmentService.createTreatment(treatment));
      mockVenues.forEach(venue => venueService.createVenue(venue));

      // 直接使用 mockAppointments、mockTreatmentRecords、mockWorkOrders
      mockAppointments.forEach(appointment => appointmentService.createAppointment(appointment));
      mockTreatmentRecords.forEach(record => treatmentRecordService.createTreatmentRecord(record));
      // 如有工单服务，可在此写入 mockWorkOrders
      // mockWorkOrders.forEach(order => workOrderService.createWorkOrder(order));

      return {
        success: true,
        message: '示例数据已生成',
        counts: {
          customers: customerService.getCustomerCount(),
          practitioners: practitionerService.getPractitionerCount(),
          treatments: treatmentService.getTreatmentCount(),
          venues: venueService.getVenueCount(),
          appointments: appointmentService.getAppointmentCount(),
          treatmentRecords: treatmentRecordService.getTreatmentRecordCount()
        }
      };
    } catch (error) {
      console.error('Error generating sample data:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

// Create singleton instance
const dataService = new DataService();

export default dataService;